import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import PromptInputBar from './PromptInputBar';
import CodeEditorPanel from './CodeEditorPanel';
import TaskPanel from './TaskPanel';
import { useProjectStore } from '../../stores/projectStore';
import { useAgentConfigStore } from '../../stores/agentConfigStore';
import { useTaskStore } from '../../stores/taskStore';

import { AgentWebSocket, WorkflowWebSocket } from '../../services/websocket';
import { workflowApi, projectApi } from '../../services/api';
import { getAuthToken } from '../../utils/auth';
import { formatErrorMessage } from '../../utils/errorUtils';
import DesignQuestionnaire, { DesignQuestionnaireSummary } from './DesignQuestionnaire';

/**
 * BuilderWorkspace - Main workspace for AI-powered web building
 */
// Device dimensions for responsive preview
const DEVICE_SIZES = {
  mobile: { width: 375, height: 667, label: 'iPhone SE' },
  tablet: { width: 768, height: 1024, label: 'iPad' },
  desktop: { width: '100%', height: '100%', label: 'Desktop' }
};

function BuilderWorkspace({ experience, initialPrompt, onNewChat, chatSidebarOpen = true, viewMode = 'preview', deviceMode = 'desktop' }) {
  // Project store
  const {
    currentProject,
    viteUrl,
    viteStatus,
    viteLoading,
    viteLoadingMessage,
    startVite,
    stopVite,
    checkViteStatus,
    loadFiles,
    getProjectMessages,
    setProjectMessages,
    loadMessages,
    undoToMessage,
    setStreaming,
  } = useProjectStore();
  
  // Undo modal state
  const [undoModalOpen, setUndoModalOpen] = useState(false);
  const [undoTargetMessage, setUndoTargetMessage] = useState(null);
  const [undoInProgress, setUndoInProgress] = useState(false);

  // Unique ID generator to avoid React key conflicts
  const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Agent config from Designer
  const { getAgentConfig } = useAgentConfigStore();

  // Task store for tracking objectives and active agent
  const { tasks, loadTasks, handleTaskEvent, clearTasks, setActiveAgent, activeAgent } = useTaskStore();

  // Local state - initialize with stored messages for current project
  const [messages, setMessagesLocal] = useState([]);
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [currentAttachments, setCurrentAttachments] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [contentBlocks, setContentBlocks] = useState([]); // Ordered list of {type: 'text'|'tool', ...}
  const [expandedTools, setExpandedTools] = useState({}); // Track which completed tools are expanded
  const [error, setError] = useState(null);
  const [previewKey, setPreviewKey] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null); // For lightbox modal
  // activeAgent is now in taskStore for shared access
  const [sessionId, setSessionId] = useState(null); // Session ID for task tracking

  // Refs for accumulating data during streaming (content blocks approach)
  const contentBlocksRef = useRef([]); // [{type: 'text', content: '...'}, {type: 'tool', ...}]
  const lastAgentRef = useRef(null); // Track last agent for transition markers
  const pendingAgentRef = useRef(null); // Batched agent name update, flushed in RAF cycle
  const toolInputAccRef = useRef({}); // Accumulates input_json_delta per toolUseId
  const sessionIdRef = useRef(null); // Ref for sessionId to avoid stale closures in callbacks
  const rafRef = useRef(null); // requestAnimationFrame handle for throttled rendering
  const handledInterruptIdsRef = useRef(new Set()); // Prevents duplicate interrupt UI on reconnect

  // Throttled content update: mutations happen on contentBlocksRef (instant, no re-render),
  // then this schedules a single React state update per animation frame (~60fps).
  // Also flushes any pending setActiveAgent to avoid extra re-renders in the hot path.
  const scheduleContentUpdate = useCallback(() => {
    if (rafRef.current) return; // already scheduled
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      if (pendingAgentRef.current !== undefined) {
        setActiveAgent(pendingAgentRef.current);
        pendingAgentRef.current = undefined;
      }
      setContentBlocks([...contentBlocksRef.current]);
    });
  }, [setActiveAgent]);

  // Cancel any pending rAF on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Force iframe remount when currentProject changes.
  // In cloud mode the viteUrl is the same for all projects (contains session_id,
  // not project_id), so without this the browser would serve a stale cached page.
  useEffect(() => {
    if (currentProject?.id) {
      setPreviewKey(k => k + 1);
    }
  }, [currentProject?.id]);

  // Keep sessionIdRef in sync with sessionId state
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  // WebSocket ref
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const userScrolledUpRef = useRef(false); // Track if user manually scrolled up
  const hasInitializedRef = useRef(false);
  const isGeneratingRef = useRef(false);  // Ref to avoid stale closures
  const previewIframeRef = useRef(null);  // Reference to preview iframe
  const fileRefreshTimeoutRef = useRef(null);  // Debounce file refreshes
  const pendingFileRefreshRef = useRef(false);  // Track if refresh is pending
  // Task updates now come via WebSocket tool results - no polling needed
  const processedToolResultsRef = useRef(new Set());  // Track processed tool results to avoid duplicates
  const hasCheckedActiveWorkflowRef = useRef(false);  // Prevent duplicate active-workflow checks on mount

  // Debounced file refresh - only refreshes once after 2 seconds of no new requests
  const scheduleFileRefresh = useCallback(() => {
    pendingFileRefreshRef.current = true;

    // Clear any existing timeout
    if (fileRefreshTimeoutRef.current) {
      clearTimeout(fileRefreshTimeoutRef.current);
    }

    // Schedule refresh after 2 seconds of no new file operations
    fileRefreshTimeoutRef.current = setTimeout(() => {
      if (pendingFileRefreshRef.current) {
        loadFiles();
        pendingFileRefreshRef.current = false;
      }
    }, 2000);
  }, [loadFiles]);

  // Immediate file refresh (for when streaming ends)
  const flushFileRefresh = useCallback(() => {
    if (fileRefreshTimeoutRef.current) {
      clearTimeout(fileRefreshTimeoutRef.current);
      fileRefreshTimeoutRef.current = null;
    }
    loadFiles();
    pendingFileRefreshRef.current = false;
  }, [loadFiles]);

  // Debounced store sync — local state updates instantly, but store writes
  // are batched to avoid excessive object spreads during rapid updates.
  const storeSyncTimerRef = useRef(null);
  const pendingStoreSyncRef = useRef(null);

  const setMessages = useCallback((updater) => {
    setMessagesLocal(prev => {
      const newMessages = typeof updater === 'function' ? updater(prev) : updater;
      if (currentProject?.id) {
        pendingStoreSyncRef.current = { projectId: currentProject.id, messages: newMessages };
        if (!storeSyncTimerRef.current) {
          storeSyncTimerRef.current = setTimeout(() => {
            storeSyncTimerRef.current = null;
            const pending = pendingStoreSyncRef.current;
            if (pending) {
              setProjectMessages(pending.projectId, pending.messages);
              pendingStoreSyncRef.current = null;
            }
          }, 300);
        }
      }
      return newMessages;
    });
  }, [currentProject?.id, setProjectMessages]);

  // Cleanup timeouts, intervals, and WebSocket on unmount
  useEffect(() => {
    return () => {
      if (fileRefreshTimeoutRef.current) {
        clearTimeout(fileRefreshTimeoutRef.current);
      }
      if (storeSyncTimerRef.current) {
        clearTimeout(storeSyncTimerRef.current);
        // Flush pending store sync before unmount
        const pending = pendingStoreSyncRef.current;
        if (pending) setProjectMessages(pending.projectId, pending.messages);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setStreaming(false);
    };
  }, [setStreaming, setProjectMessages]);

  // Track the last project ID to detect actual project changes
  const lastProjectIdRef = useRef(null);

  // Load messages when project changes via local backend gateway
  useEffect(() => {
    const loadProjectData = async () => {
      if (currentProject?.id) {
        const isNewProject = lastProjectIdRef.current !== currentProject.id;

        if (isNewProject) {
          clearTasks();
          const newSessionId = `${currentProject.id.slice(0, 8)}-${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 4)}`;
          setSessionId(newSessionId);
          sessionIdRef.current = newSessionId;
          lastProjectIdRef.current = currentProject.id;
          console.log(`[BuilderWorkspace] New project ${currentProject.id}, session: ${newSessionId}`);
          loadTasks(currentProject.id, newSessionId);
        }

        // Load messages via local backend (which fetches from AWS or its cache)
        console.log('[BuilderWorkspace] Loading messages via local backend...');
        const loaded = await loadMessages(currentProject.id);
        const mappedMessages = (loaded || []).map(m => ({
          id: m.id,
          type: m.role === 'user' ? 'user' : 'assistant',
          content: m.content,
          attachments: m.attachments || [],
          contentBlocks: m.contentBlocks || [],
          commitShaBefore: m.commitShaBefore,
          commitShaAfter: m.commitShaAfter,
        }));
        setMessagesLocal(mappedMessages);
        hasInitializedRef.current = mappedMessages.length > 0;
        console.log(`[BuilderWorkspace] Loaded ${mappedMessages.length} messages`);
      } else {
        setMessagesLocal([]);
        hasInitializedRef.current = false;
        setSessionId(null);
        sessionIdRef.current = null;
        clearTasks();
        lastProjectIdRef.current = null;
      }
    };

    loadProjectData();
  }, [currentProject?.id, currentProject?.aws_project_id, getProjectMessages, loadMessages, loadTasks, clearTasks]);

  // Initialize with first prompt (only once per project, only if no existing messages)
  useEffect(() => {
    if (initialPrompt && initialPrompt.trim() && messages.length === 0 && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      // User message will appear via message_created from backend
      setTimeout(() => executeAgent(initialPrompt), 100);
    }
  }, [initialPrompt, messages.length]);

  // Check Vite when project changes - auto-start if not running.
  // Debounced to avoid double-starting when setCurrentProject already started Vite.
  useEffect(() => {
    if (!currentProject) return;
    const timer = setTimeout(() => {
      const store = useProjectStore.getState();
      if (store.viteLoading || store.viteStatus?.running) return;
      checkViteStatus().then(() => {
        const s = useProjectStore.getState();
        if (!s.viteStatus?.running && !s.viteLoading) {
          startVite().catch(err => console.error('Auto-start Vite failed:', err));
        }
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [currentProject, checkViteStatus, startVite]);

  // Handle scroll to detect if user scrolled up
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    
    // Check if user is near the bottom (within 100px)
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    userScrolledUpRef.current = !isNearBottom;
  }, []);

  // Auto-scroll messages - only if user hasn't scrolled up
  useEffect(() => {
    if (!userScrolledUpRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, contentBlocks]);

  const stripGeminiCtrl = (text) => text.replace(/<ctrl\d+>/g, '');

  /**
   * Normalize message content to a plain string.
   * Handles: string, array of content blocks [{text: "..."}], or stringified array.
   */
  const normalizeContent = (content) => {
    if (!content) return '';
    if (typeof content === 'string') return stripGeminiCtrl(content);
    if (Array.isArray(content)) {
      return stripGeminiCtrl(content
        .filter(b => typeof b === 'object' && b !== null && (b.text || (b.type === 'text' && b.text)))
        .map(b => b.text || '')
        .join(''));
    }
    if (typeof content === 'object' && content.text) {
      return stripGeminiCtrl(content.text);
    }
    return stripGeminiCtrl(String(content));
  };

  /**
   * Safely extract text from various event formats
   */
  const extractTextSafe = (event) => {
    if (!event) return '';

    // If it's already a string, return it
    if (typeof event === 'string') return stripGeminiCtrl(event);

    // Try various known paths for text content
    try {
      // Multi-agent/Swarm events may wrap the actual event
      // Check for nested event structure from Swarm
      const innerEvent = event.event || event;

      // Direct text fields
      if (typeof innerEvent.text === 'string') return stripGeminiCtrl(innerEvent.text);
      if (typeof innerEvent.content === 'string') return stripGeminiCtrl(innerEvent.content);

      // Nested in data
      if (innerEvent.data) {
        if (typeof innerEvent.data === 'string') return stripGeminiCtrl(innerEvent.data);
        if (typeof innerEvent.data.text === 'string') return stripGeminiCtrl(innerEvent.data.text);
        if (typeof innerEvent.data.content === 'string') return stripGeminiCtrl(innerEvent.data.content);
      }

      // Delta format (streaming)
      if (innerEvent.delta) {
        if (typeof innerEvent.delta.text === 'string') return stripGeminiCtrl(innerEvent.delta.text);
        if (typeof innerEvent.delta === 'string') return stripGeminiCtrl(innerEvent.delta);
      }

      // Anthropic content block format
      if (innerEvent.contentBlockDelta?.delta?.text) {
        return stripGeminiCtrl(innerEvent.contentBlockDelta.delta.text);
      }
      if (innerEvent.content_block_delta?.delta?.text) {
        return stripGeminiCtrl(innerEvent.content_block_delta.delta.text);
      }

      // Message content (for complete messages)
      if (innerEvent.message?.content) {
        if (typeof innerEvent.message.content === 'string') {
          return stripGeminiCtrl(innerEvent.message.content);
        }
        // Could be array of content blocks
        if (Array.isArray(innerEvent.message.content)) {
          return stripGeminiCtrl(innerEvent.message.content
            .filter(b => b.type === 'text' || b.text)
            .map(b => b.text)
            .join(''));
        }
      }
    } catch (e) {
      console.error('Error extracting text:', e);
    }

    return '';
  };

  /**
   * Extract tool call info from event
   */
  const extractToolCall = (event) => {
    if (!event) return null;

    try {
      // Multi-agent/Swarm events may wrap the actual event
      const innerEvent = event.event || event;

      // Strands SDK ToolUseStreamEvent format (most common)
      // These events have: { current_tool_use: {...}, delta: {...} }
      // Backend strips current_tool_use.input (the growing accumulated string)
      // and sends only delta.input_json_delta (~50 bytes). We accumulate locally.
      if (innerEvent.current_tool_use) {
        const toolUse = innerEvent.current_tool_use;
        const toolId = toolUse.toolUseId || toolUse.id || '_default';
        const delta = innerEvent.delta;
        const deltaChunk = delta?.input_json_delta;

        if (deltaChunk) {
          const acc = toolInputAccRef.current;
          acc[toolId] = (acc[toolId] || '') + deltaChunk;
        }

        const rawInput = toolInputAccRef.current[toolId] || (typeof toolUse.input === 'string' ? toolUse.input : null);

        // During streaming (delta present), skip expensive JSON.parse — the
        // regex path in handleAgentEvent extracts what it needs cheaply.
        // Only parse when there is no delta (first/final event) or input is small.
        let toolInput = {};
        if (!deltaChunk && rawInput && rawInput.length < 4096) {
          try { toolInput = JSON.parse(rawInput); } catch { /* incomplete */ }
        } else if (!rawInput && toolUse.input && typeof toolUse.input === 'object') {
          toolInput = toolUse.input;
        }

        return {
          name: toolUse.name || toolUse.tool_name || 'unknown',
          input: toolInput,
          rawInput: rawInput,
        };
      }

      // Direct tool fields
      if (innerEvent.tool_name || innerEvent.toolName) {
        let toolInput = innerEvent.tool_input || innerEvent.toolInput || innerEvent.input || {};
        if (typeof toolInput === 'string') {
          try { toolInput = JSON.parse(toolInput); } catch { toolInput = {}; }
        }
        return {
          name: innerEvent.tool_name || innerEvent.toolName,
          input: toolInput,
        };
      }

      // tool_use format (Anthropic direct)
      if (innerEvent.tool_use) {
        let toolInput = innerEvent.tool_use.input || {};
        if (typeof toolInput === 'string') {
          try { toolInput = JSON.parse(toolInput); } catch { toolInput = {}; }
        }
        return {
          name: innerEvent.tool_use.name,
          input: toolInput,
        };
      }

      // contentBlockStart with tool_use
      if (innerEvent.contentBlockStart?.content_block?.type === 'tool_use') {
        return {
          name: innerEvent.contentBlockStart.content_block.name,
          input: {},
        };
      }

      // content_block format
      if (innerEvent.content_block?.type === 'tool_use') {
        let toolInput = innerEvent.content_block.input || {};
        if (typeof toolInput === 'string') {
          try { toolInput = JSON.parse(toolInput); } catch { toolInput = {}; }
        }
        return {
          name: innerEvent.content_block.name,
          input: toolInput,
        };
      }

      // Check for tool result events
      if (innerEvent.tool_result || innerEvent.toolResult || innerEvent.type === 'tool_result') {
        let result = innerEvent.tool_result || innerEvent.toolResult || innerEvent.result;
        
        // Some SDKs nest result in content
        if (!result && innerEvent.content) {
          result = innerEvent.content;
        }
        
        const toolUseId = innerEvent.tool_use_id || innerEvent.toolUseId;
        if (toolUseId) delete toolInputAccRef.current[toolUseId];
        
        return {
          name: 'tool_result',
          result: result,
          toolUseId: toolUseId,
        };
      }
      
      // Strands SDK tool_result format
      if (innerEvent.type === 'tool_result_event' || innerEvent.tool_result_event) {
        const trEvent = innerEvent.tool_result_event || innerEvent;
        const toolUseId = trEvent.tool_use_id;
        if (toolUseId) delete toolInputAccRef.current[toolUseId];
        return {
          name: 'tool_result',
          result: trEvent.result || trEvent.content,
          toolUseId: toolUseId,
        };
      }

      // Handoffs and delegations are internal control flow, skip parsing them as visual tools
      if (innerEvent.current_tool_use?.name?.startsWith('handoff_to_') || innerEvent.tool_name?.startsWith('handoff_to_') ||
          innerEvent.current_tool_use?.name?.startsWith('delegate_to_') || innerEvent.tool_name?.startsWith('delegate_to_')) {
        return null;
      }

    } catch (e) {
      console.error('Error extracting tool call:', e);
    }

    return null;
  };



  /**
   * Handle incoming agent events (shared logic)
   */
  const handleAgentEvent = useCallback((event) => {
    // Check if this is the final result event from Strands SDK
    if (event.result || event.type === 'result' || event.type === 'agent_result') {
      console.log('Received result event, agent completing');
      return;
    }

    const blocks = contentBlocksRef.current;

    // Helper to get or create current text block
    const getOrCreateTextBlock = () => {
      const lastBlock = blocks[blocks.length - 1];
      if (lastBlock && lastBlock.type === 'text' && !lastBlock.completed) {
        return lastBlock;
      }
      // Mark previous block as completed if it exists
      if (lastBlock && !lastBlock.completed) {
        lastBlock.completed = true;
      }
      // Create new text block
      const newBlock = { type: 'text', id: generateId(), content: '', completed: false };
      blocks.push(newBlock);
      return newBlock;
    };

    // Helper to get or create tool group block
    const getOrCreateToolGroup = (toolType) => {
      const lastBlock = blocks[blocks.length - 1];
      if (lastBlock && lastBlock.type === 'toolGroup' && lastBlock.toolType === toolType && !lastBlock.completed) {
        return lastBlock;
      }
      // Mark previous block as completed if it exists
      if (lastBlock && !lastBlock.completed) {
        lastBlock.completed = true;
      }
      const newBlock = {
        type: 'toolGroup',
        id: generateId(),
        toolType,
        items: [],
        completed: false
      };
      blocks.push(newBlock);
      return newBlock;
    };

    // Handle delta events (streaming text)
    if (event.delta && event.delta.text) {
      const textBlock = getOrCreateTextBlock();
      textBlock.content += event.delta.text;
      scheduleContentUpdate();
      return;
    }

    // Handle data.text format (single-agent SDK events)
    if (event.data && typeof event.data === 'object' && typeof event.data.text === 'string') {
      const textBlock = getOrCreateTextBlock();
      textBlock.content += event.data.text;
      scheduleContentUpdate();
      return;
    }

    // Handle older event formats
    if (event.type === 'text' && event.text) {
      const textBlock = getOrCreateTextBlock();
      textBlock.content += event.text;
      scheduleContentUpdate();
      return;
    }

    // Extract tool calls
    const toolCall = extractToolCall(event);
    if (toolCall) {
      // Handle Tool Results (specifically for images)
      if (toolCall.name === 'tool_result') {
        const result = toolCall.result;

        // Check if this is an image result from generate_image
        // Structure: { status: 'success', image_data: { format: 'png', bytes: 'base64...' } }
        // Or sometimes nested in content list
        let imageData = null;

        // Direct dict
        if (result?.image_data?.bytes) {
          imageData = result.image_data;
        }
        // Content list (common in Bedrock/Anthropic)
        else if (Array.isArray(result?.content)) {
          const imgBlock = result.content.find(b => b.type === 'image');
          if (imgBlock) imageData = imgBlock.source; // { type: 'base64', media_type: ..., data: ... }
        }

        if (imageData) {
          const base64Data = imageData.bytes || imageData.data;
          const mimeType = imageData.format === 'png' ? 'image/png' : (imageData.media_type || 'image/png');
          const dataUrl = `data:${mimeType};base64,${base64Data}`;

          // Add image block
          const newBlock = {
            type: 'image',
            id: generateId(),
            src: dataUrl,
            alt: 'Generated Design',
            completed: true
          };
          blocks.push(newBlock);
          scheduleContentUpdate();
        }
        return; // Done handling result
      }

      if (toolCall.name.startsWith('handoff_to_') || toolCall.name.startsWith('delegate_to_')) {
        // Will be handled by agent marker events, skip visual tool display
        return;
      }

      // Handle Tool Uses (normal flow)
      if (toolCall.name !== 'tool_result') {
        // Map tool names to friendly display names
        const toolDisplayNames = {
          'sandboxed_file_read': 'Reading',
          'sandboxed_file_write': 'Writing',
          'sandboxed_shell': 'Running',
          'sandboxed_editor': 'Editing',
          'browser': 'Inspecting Preview',
          'u3d_browser': 'Virtual Browser',
          'unlimited3d_kb': 'Searching Docs',
          'search_unlimited3d_knowledge_base': 'Searching Docs',
          'threejs_kb': 'Searching Three.js Docs',
          'search_threejs_knowledge_base': 'Searching Three.js Docs',
          'generate_image': 'Generating Image',
          'julian_generate': 'Generating 3D Model',
          'polyhaven': 'Fetching Assets',
          'model_optimizer': 'Optimizing Model',
          'tasks': 'Planning'
        };

        const iconMap = {
          'sandboxed_file_read': 'file',
          'sandboxed_file_write': 'file',
          'sandboxed_shell': 'terminal',
          'sandboxed_editor': 'file',
          'browser': 'globe',
          'u3d_browser': 'globe',
          'unlimited3d_kb': 'search',
          'search_unlimited3d_knowledge_base': 'search',
          'threejs_kb': 'search',
          'search_threejs_knowledge_base': 'search',
          'generate_image': 'image',
          'julian_generate': 'image',
          'polyhaven': 'image',
          'model_optimizer': 'image'
        };

        const toolType = toolCall.name;
        const input = toolCall.input || {};

        // Get display info based on tool type
        let displayInfo = '';
        if (toolType.includes('file') || toolType.includes('editor')) {
          const path = input.path || input.file_path || input.filename || '';
          displayInfo = path ? path.split('/').pop().split('\\').pop() : '';
        } else if (toolType === 'sandboxed_shell') {
          const cmd = input.command || '';
          displayInfo = cmd.length > 40 ? cmd.substring(0, 40) + '...' : cmd;
        } else if (toolType === 'browser') {
          const action = input.action || '';
          const actionLabels = {
            'screenshot': 'Taking Screenshot',
            'evaluate': 'Running JavaScript',
            'navigate': 'Navigating',
            'click': 'Clicking Element',
            'type': 'Typing',
            'console_logs': `Reading Logs${input.level ? ` (${input.level})` : ''}`,
            'page_errors': 'Checking JS Errors',
            'network_errors': 'Checking Network',
            'resize': 'Resizing Viewport',
            'reload': 'Reloading Page',
            'snapshot': 'Getting DOM Snapshot',
            'start': 'Starting Browser',
            'stop': 'Stopping Browser',
          };
          displayInfo = actionLabels[action] || action;
        } else if (toolType === 'u3d_browser') {
          const action = input.action || '';
          const actionLabels = {
            'screenshot': 'Taking Screenshot',
            'query_scene': `Querying: ${input.method || 'scene'}`,
            'wait_for_u3d': 'Waiting for 3D Engine',
            'navigate': 'Navigating',
            'click': 'Clicking Element',
            'type': 'Typing',
            // Debugging actions
            'console_logs': `Reading Logs${input.level ? ` (${input.level})` : ''}`,
            'page_errors': 'Checking JS Errors',
            'network_errors': 'Checking Network Errors',
            'resize': `Resizing: ${input.preset || `${input.width}x${input.height}`}`,
            'reload': input.hard ? 'Hard Reloading' : 'Reloading Page',
          };
          displayInfo = actionLabels[action] || action || '';
        } else if (toolType === 'search_unlimited3d_knowledge_base' || 
                   (input.query && (input.top_k !== undefined || input.filters !== undefined))) {
          // Built-in KB or custom KB (detected by query + top_k/filters params)
          displayInfo = input.query ? input.query.substring(0, 40) + '...' : '';
        }

        // Get or create tool group for this tool type
        const toolGroup = getOrCreateToolGroup(toolType);
        
        // Check if this is a custom KB tool (has query + top_k/filters params)
        const isCustomKbTool = input.query && (input.top_k !== undefined || input.filters !== undefined);
        
        toolGroup.icon = iconMap[toolType] || (isCustomKbTool ? 'search' : 'code');
        toolGroup.displayName = toolDisplayNames[toolType] || (isCustomKbTool ? 'Knowledge Base' : toolType);
        
        // Store streaming content for live preview based on tool type.
        // rawInput grows with each delta token; we only re-run expensive regex
        // extraction when it has grown by EXTRACT_THRESHOLD chars since last run.
        const EXTRACT_THRESHOLD = 1000;
        const rawInput = toolCall.rawInput || '';
        let streamingContent = '';
        let streamingLabel = toolType;

        // Skip regex extraction if rawInput hasn't grown enough since last run
        const lastLen = toolGroup._lastExtractLen || 0;
        const needsExtract = !rawInput || (rawInput.length - lastLen >= EXTRACT_THRESHOLD) || rawInput.length < EXTRACT_THRESHOLD;
        
        if (toolType === 'sandboxed_file_write' || toolType === 'sandboxed_editor') {
          // Only extract the file path — skip content preview entirely.
          // File content deltas are the main cause of frontend lag (the
          // rawInput string grows to 50-100KB and every slice/replace stalls
          // the main thread). The path alone is enough context for the user.
          streamingLabel = input.path || input.file_path || toolGroup.streamingPath || 'Writing file...';
          if (!toolGroup._extractedPath && rawInput) {
            const head = rawInput.slice(0, 500);
            const pathMatch = head.match(/"path"\s*:\s*"([^"]+)"/);
            if (pathMatch) toolGroup._extractedPath = pathMatch[1];
          }
          if (toolGroup._extractedPath) streamingLabel = toolGroup._extractedPath;
        } else if (toolType === 'sandboxed_shell') {
          streamingContent = input.command || '';
          streamingLabel = 'Shell command';
          if (!streamingContent && rawInput && needsExtract) {
            const cmdMatch = rawInput.match(/"command"\s*:\s*"([^"]+)"/);
            if (cmdMatch) streamingContent = cmdMatch[1];
            toolGroup._lastExtractLen = rawInput.length;
          }
        } else if (toolType === 'tasks') {
          const action = input.action || '';
          if (action === 'plan' || rawInput.includes('"plan"')) {
            streamingContent = input.description || '';
            streamingLabel = 'Planning tasks';
            if (!streamingContent && rawInput && needsExtract) {
              const descMatch = rawInput.match(/"description"\s*:\s*"((?:[^"\\]|\\.)*)(?:"|$)/);
              if (descMatch) streamingContent = descMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"');
              toolGroup._lastExtractLen = rawInput.length;
            }
          } else if (action === 'add') {
            streamingContent = input.description || '';
            streamingLabel = 'Adding task';
          } else if (action === 'done') {
            streamingContent = `Completing: ${input.task_id || ''}`;
            streamingLabel = 'Task progress';
          } else if (action === 'list') {
            streamingLabel = 'Checking tasks';
          }
        } else if (toolType === 'u3d_browser') {
          if (input.javascript) {
            streamingContent = input.javascript;
            streamingLabel = 'JavaScript execution';
          } else if (input.method) {
            streamingContent = `${input.method}(${JSON.stringify(input.params || {})})`;
            streamingLabel = 'Querying 3D scene';
          } else if (rawInput && needsExtract) {
            const jsMatch = rawInput.match(/"javascript"\s*:\s*"((?:[^"\\]|\\.)*)(?:"|$)/);
            if (jsMatch) {
              streamingContent = jsMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"');
              streamingLabel = 'JavaScript execution';
            }
            toolGroup._lastExtractLen = rawInput.length;
          }
        } else if (toolType === 'search_unlimited3d_knowledge_base' || 
                   (input.query && (input.top_k !== undefined || input.filters !== undefined))) {
          streamingContent = input.query || '';
          streamingLabel = 'Knowledge Base Query';
          toolGroup.isKbTool = true;
        } else if (toolType === 'generate_image' || toolType === 'generate_image_pro') {
          streamingContent = input.prompt || '';
          streamingLabel = 'Image prompt';
        } else if (toolType === 'handoff_to_agent') {
          streamingContent = input.message || '';
          streamingLabel = `Handing off to ${input.agent_name || 'agent'}`;
          if (!streamingContent && rawInput && needsExtract) {
            const msgMatch = rawInput.match(/"message"\s*:\s*"((?:[^"\\]|\\.)*)(?:"|$)/);
            if (msgMatch) streamingContent = msgMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"');
            const agentMatch = rawInput.match(/"agent_name"\s*:\s*"([^"]+)"/);
            if (agentMatch) streamingLabel = `Handing off to ${agentMatch[1]}`;
            toolGroup._lastExtractLen = rawInput.length;
          }
        }
        
        if (streamingContent) {
          toolGroup.streamingContent = streamingContent;
        }
        if (streamingLabel !== toolType || !toolGroup.streamingPath) {
          toolGroup.streamingPath = streamingLabel;
        }
        toolGroup.toolInput = input;

        // For file write/edit, skip storing rawInput on the block and skip
        // re-renders for delta events — only the path label matters visually.
        const isFileOp = toolType === 'sandboxed_file_write' || toolType === 'sandboxed_editor';
        if (!isFileOp) {
          toolGroup.rawInput = rawInput;
        }

        // Add to items if not duplicate
        if (displayInfo && !toolGroup.items.includes(displayInfo)) {
          toolGroup.items.push(displayInfo);
        } else if (!displayInfo && toolGroup.items.length === 0) {
          toolGroup.items.push('');
        }

        // File write/edit: only re-render when the path first appears or
        // items change — not on every content delta token.
        if (isFileOp) {
          if (toolGroup._pathRendered) return;
          if (toolGroup._extractedPath) toolGroup._pathRendered = true;
          scheduleFileRefresh();
        }

        scheduleContentUpdate();
      }
    }
  }, []);

  // Execute agent with WebSocket
  const executeAgent = useCallback(async (task, attachments = [], { resumeProjectId } = {}) => {
    if (!currentProject) {
      setError('No project selected. Please create or select a project first.');
      return;
    }

    // Prevent duplicate connections
    if (isGenerating || wsRef.current) {
      console.log('Already executing, skipping duplicate request');
      return;
    }

    setIsGenerating(true);
    isGeneratingRef.current = true;  // Keep ref in sync
    setStreaming(true);
    setError(null);
    setContentBlocks([]);

    // Generate or reuse session_id for task tracking
    let currentSessionId = sessionIdRef.current || sessionId;
    if (!currentSessionId) {
      currentSessionId = `${currentProject.id.slice(0, 8)}-${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 6)}`;
      setSessionId(currentSessionId);
      sessionIdRef.current = currentSessionId; // Update ref immediately for callbacks
    }

    // Load existing tasks for this session
    if (currentProject?.id && currentSessionId) {
      loadTasks(currentProject.id, currentSessionId);
    }
    
    console.log('[BuilderWorkspace] Running workflow with sessionId:', currentSessionId);
    
    // Reset refs for new execution
    contentBlocksRef.current = [];
    lastAgentRef.current = null;
    toolInputAccRef.current = {};
    processedToolResultsRef.current.clear();
    handledInterruptIdsRef.current.clear();

    // Create WebSocket connection (Workflow for multi-agent support)
    const ws = new WorkflowWebSocket({
      onWorkflowStart: (data) => {
        // Workflow started
        console.log('Workflow started:', data.workflow_type);
        setActiveAgent('Orchestrator'); // Default to Orchestrator initially
      },

      onWorkflowEvent: (event) => {
        // Check if this is the final result event from Strands SDK
        if (event.result || event.type === 'result' || event.type === 'agent_result') {
          pendingAgentRef.current = null;
          scheduleContentUpdate();
          return;
        }

        // Handle task events from agent tool usage (legacy format)
        if (event.type === 'task_event' || (event.data && event.data.type === 'task_event')) {
          const taskEvent = event.type === 'task_event' ? event : event.data;
          handleTaskEvent(taskEvent);
          return;
        }

        // Handle delegation start/end markers (sub-agent delegation system).
        // These are pure marker events — process agent switch and return early.
        if (event.delegation_start || event.delegation_end) {
          const delegAgent = event.current_agent;
          if (delegAgent && delegAgent !== lastAgentRef.current) {
            const displayName = delegAgent.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
            const label = event.delegation_start
              ? `Delegating to ${displayName}`
              : displayName;
            pendingAgentRef.current = displayName;

            const blocks = contentBlocksRef.current;
            const lastBlock = blocks[blocks.length - 1];
            if (lastBlock && !lastBlock.completed) {
              lastBlock.completed = true;
            }
            blocks.push({
              type: 'agentMarker',
              id: generateId(),
              agentName: label,
              agentId: delegAgent,
              completed: true,
            });
            scheduleContentUpdate();
            lastAgentRef.current = delegAgent;
          }
          return;
        }

        // Track active agent from event metadata and add visual markers for transitions.
        // Backend sends 'current_agent', also check legacy fields.
        // Agent updates are batched into the RAF cycle via pendingAgentRef.
        const agentName = event.current_agent || event.sender || event.source || event.agent_name ||
          (event.data && (event.data.current_agent || event.data.sender));
        if (agentName && typeof agentName === 'string') {
          if (lastAgentRef.current !== agentName) {
            const displayName = agentName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
            pendingAgentRef.current = displayName;

            const blocks = contentBlocksRef.current;
            const lastBlock = blocks[blocks.length - 1];
            if (lastBlock && !lastBlock.completed) {
              lastBlock.completed = true;
            }
            blocks.push({
              type: 'agentMarker',
              id: generateId(),
              agentName: displayName,
              agentId: agentName,
              completed: true
            });
            scheduleContentUpdate();
            lastAgentRef.current = agentName;
          }
        }

        const blocks = contentBlocksRef.current;

        // Helper to get or create current text block
        const getOrCreateTextBlock = () => {
          const lastBlock = blocks[blocks.length - 1];
          if (lastBlock && lastBlock.type === 'text' && !lastBlock.completed) {
            return lastBlock;
          }
          // Mark previous block as completed if it exists
          if (lastBlock && !lastBlock.completed) {
            lastBlock.completed = true;
          }
          // Create new text block
          const newBlock = { type: 'text', id: generateId(), content: '', completed: false };
          blocks.push(newBlock);
          return newBlock;
        };

        // Helper to get or create tool group block
        const getOrCreateToolGroup = (toolType) => {
          const lastBlock = blocks[blocks.length - 1];
          if (lastBlock && lastBlock.type === 'toolGroup' && lastBlock.toolType === toolType && !lastBlock.completed) {
            return lastBlock;
          }
          // Mark previous block as completed if it exists
          if (lastBlock && !lastBlock.completed) {
            lastBlock.completed = true;
          }
          // Create new tool group block
          const newBlock = {
            type: 'toolGroup',
            id: generateId(),
            toolType: toolType,
            items: [],
            icon: 'code',
            completed: false,
          };
          blocks.push(newBlock);
          return newBlock;
        };

        // Extract text safely
        const text = extractTextSafe(event);
        if (text) {
          const textBlock = getOrCreateTextBlock();
          textBlock.content += text;
          scheduleContentUpdate();
        }

        // Extract tool calls
        const toolCall = extractToolCall(event);
        if (toolCall) {
          // Handle Tool Results (specifically for images)
          if (toolCall.name === 'tool_result') {
            const result = toolCall.result;
            
            

            // Check if this is an image result from generate_image / generate_image_pro
            // New format: JSON string with { status: 'success', web_url: '/images/...', file_path: '...' }
            // Old format: { status: 'success', image_data: { format: 'png', bytes: 'base64...' } }
            let imageUrl = null;

            // Try parsing as JSON string first (new format)
            if (typeof result === 'string') {
              try {
                const parsed = JSON.parse(result);
                if (parsed.status === 'success' && parsed.web_url) {
                  // New format: use web_url directly
                  imageUrl = parsed.web_url;
                }
              } catch (e) {
                // Not JSON, ignore
              }
            }

            // Legacy: Direct dict with web_url
            if (!imageUrl && result?.status === 'success' && result?.web_url) {
              imageUrl = result.web_url;
            }

            // Legacy: Direct dict with image_data (base64)
            let imageData = null;
            if (!imageUrl) {
              if (result?.image_data?.bytes) {
                imageData = result.image_data;
              }
              // Content list (common in Bedrock/Anthropic)
              else if (Array.isArray(result?.content)) {
                const imgBlock = result.content.find(b => b.type === 'image');
                if (imgBlock) imageData = imgBlock.source;
              }

              if (imageData) {
                const base64Data = imageData.bytes || imageData.data;
                const mimeType = imageData.format === 'png' ? 'image/png' : (imageData.media_type || 'image/png');
                imageUrl = `data:${mimeType};base64,${base64Data}`;
              }
            }

            if (imageUrl) {
              // Add image block
              const newBlock = {
                type: 'image',
                id: generateId(),
                src: imageUrl,
                alt: 'Generated Design',
                completed: true
              };
              blocks.push(newBlock);
              scheduleContentUpdate();
            }

            // Handle manage_tasks tool results - update TaskStore in real-time.
            // The result can arrive in many formats depending on SDK / model:
            //   - JSON string: '{"_task_event":"plan",...}'
            //   - Array of content blocks: [{type:"text", text:"..."}]
            //   - Object with content array: {content: [{type:"text", text:"..."}]}
            //   - Pre-parsed dict: {_task_event: "plan", ...}
            let taskResult = null;
            const tryParseTask = (val) => {
              if (!val) return null;
              if (typeof val === 'string') {
                try { return JSON.parse(val); } catch { return null; }
              }
              if (typeof val === 'object' && val._task_event) return val;
              return null;
            };

            taskResult = tryParseTask(result);
            if (!taskResult && Array.isArray(result)) {
              const tb = result.find(b => b.type === 'text' || b.text);
              if (tb) taskResult = tryParseTask(tb.text || tb);
            }
            if (!taskResult && result?.content) {
              if (Array.isArray(result.content)) {
                const tb = result.content.find(b => b.type === 'text' || b.text);
                if (tb) taskResult = tryParseTask(tb.text || tb);
              } else {
                taskResult = tryParseTask(result.content);
              }
            }

            // Detect tasks tool results using _task_event marker
            // Backend adds _task_event: 'plan'|'add'|'done'|'update'|'list' to all task results
            if (taskResult && taskResult._task_event) {

              // Handle 'add' action - single task created
              if (taskResult.task && !taskResult.completed) {
                const currentTasks = useTaskStore.getState().tasks;
                const taskData = {
                  id: taskResult.task.id,
                  content: taskResult.task.description || taskResult.task.content,
                  status: taskResult.task.status || 'pending',
                  ...taskResult.task
                };
                const existingTask = currentTasks.find(t => t.id === taskData.id);
                
                if (existingTask) {
                  handleTaskEvent({
                    event_type: 'task_updated',
                    data: { task: taskData }
                  });
                } else {
                  handleTaskEvent({
                    event_type: 'task_added',
                    data: { task: taskData }
                  });
                }
              }
              
              // Handle 'plan' action - multiple tasks created
              if (taskResult.tasks && Array.isArray(taskResult.tasks) && taskResult.tasks.length > 0) {
                for (const t of taskResult.tasks) {
                  const taskData = {
                    id: t.id,
                    content: t.description || t.content || t.display,
                    status: t.status || 'pending',
                    ...t
                  };
                  handleTaskEvent({
                    event_type: 'task_added',
                    data: { task: taskData }
                  });
                }
              }
              
              // Handle 'done' action - task completed (+ next task auto-started)
              if (taskResult.completed) {
                handleTaskEvent({
                  event_type: 'task_updated',
                  data: { 
                    task: {
                      id: taskResult.completed.id,
                      content: taskResult.completed.description || taskResult.completed.content,
                      status: 'completed',
                      completed_by: taskResult.completed.completed_by
                    }
                  }
                });
                
                // If next_task was auto-started, update it too
                if (taskResult.next_task) {
                  handleTaskEvent({
                    event_type: 'task_updated',
                    data: { 
                      task: {
                        id: taskResult.next_task.id,
                        content: taskResult.next_task.description || taskResult.next_task.content,
                        status: 'in_progress'
                      }
                    }
                  });
                }
              }
            }

            // Handle KB tool results - update the last toolGroup with results
            // Check for the KB_RESULT_START marker that backend KB tools add
            if (typeof result === 'string' && result.startsWith('[KB_RESULT_START]')) {
              // Strip the marker before storing
              const cleanResult = result.replace('[KB_RESULT_START]\n', '');
              // Find the last KB toolGroup block and add the result
              for (let i = blocks.length - 1; i >= 0; i--) {
                const block = blocks[i];
                if (block.type === 'toolGroup' && 
                    (block.toolType === 'search_unlimited3d_knowledge_base' || 
                     block.toolType === 'search_threejs_knowledge_base' ||
                     block.isKbTool === true)) {
                  block.kbResult = cleanResult;
                  block.completed = true;
                  scheduleContentUpdate();
                  break;
                }
              }
            }

            // Mark the most recent uncompleted tool group as completed
            for (let i = blocks.length - 1; i >= 0; i--) {
              if (blocks[i].type === 'toolGroup' && !blocks[i].completed) {
                blocks[i].completed = true;
                scheduleContentUpdate();
                break;
              }
            }

            return; // Done handling result
          }

          // Handle Tool Uses (normal flow)
          if (toolCall.name !== 'tool_result') {
            // Map tool names to friendly display names
            const toolDisplayNames = {
              'sandboxed_file_read': 'Reading',
              'sandboxed_file_write': 'Writing',
              'sandboxed_shell': 'Running',
              'sandboxed_editor': 'Editing',
              'search_unlimited3d_knowledge_base': 'Searching KB',
              'search_threejs_knowledge_base': 'Searching Three.js Docs',
              'threejs_kb': 'Searching Three.js Docs',
              'browser': 'Inspecting Preview',
              'u3d_browser': 'Virtual Browser',
              'generate_image': '🎨 Generating Image',
              'generate_image_pro': '🎨 Generating Image (Pro)',
              'julian_generate': 'Generating 3D Model',
              'polyhaven': 'Fetching Assets',
              'model_optimizer': 'Optimizing Model',
              'tasks': 'Planning',
              'handoff_to_agent': 'Handing Off',
            };

            const iconMap = {
              'sandboxed_file_read': 'code',
              'sandboxed_file_write': 'code',
              'sandboxed_shell': 'terminal',
              'sandboxed_editor': 'code',
              'search_unlimited3d_knowledge_base': 'code',
              'search_threejs_knowledge_base': 'search',
              'threejs_kb': 'search',
              'browser': 'globe',
              'u3d_browser': 'code',
              'generate_image': 'image',
              'generate_image_pro': 'image',
              'julian_generate': 'image',
              'polyhaven': 'image',
              'model_optimizer': 'image',
              'handoff_to_agent': 'code',
            };

            const toolType = toolCall.name;
            const input = toolCall.input || {};

            // Get display info based on tool type
            let displayInfo = '';
            if (toolType.startsWith('handoff_to_') || toolType.startsWith('delegate_to_')) {
              displayInfo = input.agent_name || toolType.replace('handoff_to_', '').replace('delegate_to_', '');
            } else if (toolType.includes('file') || toolType.includes('editor')) {
              const path = input.path || input.file_path || input.filename || '';
              displayInfo = path ? path.split('/').pop().split('\\').pop() : '';
            } else if (toolType === 'sandboxed_shell') {
              const cmd = input.command || '';
              displayInfo = cmd.length > 40 ? cmd.substring(0, 40) + '...' : cmd;
            } else if (toolType === 'browser') {
              const action = input.action || '';
              const actionLabels = {
                'screenshot': 'Taking Screenshot',
                'evaluate': 'Running JavaScript',
                'navigate': 'Navigating',
                'click': 'Clicking Element',
                'type': 'Typing',
                'console_logs': `Reading Logs${input.level ? ` (${input.level})` : ''}`,
                'page_errors': 'Checking JS Errors',
                'network_errors': 'Checking Network',
                'resize': `Resizing: ${input.preset || `${input.width}x${input.height}`}`,
                'reload': input.hard ? 'Hard Reloading' : 'Reloading Page',
                'snapshot': 'Getting DOM Snapshot',
                'start': 'Starting Browser',
                'stop': 'Stopping Browser',
              };
              displayInfo = actionLabels[action] || action || '';
            } else if (toolType === 'u3d_browser') {
              const action = input.action || '';
              const actionLabels = {
                'screenshot': 'Taking Screenshot',
                'query_scene': `Querying: ${input.method || 'scene'}`,
                'wait_for_u3d': 'Waiting for 3D Engine',
                'navigate': 'Navigating',
                'click': 'Clicking Element',
                'type': 'Typing',
                'console_logs': `Reading Logs${input.level ? ` (${input.level})` : ''}`,
                'page_errors': 'Checking JS Errors',
                'network_errors': 'Checking Network Errors',
                'resize': `Resizing: ${input.preset || `${input.width}x${input.height}`}`,
                'reload': input.hard ? 'Hard Reloading' : 'Reloading Page',
              };
              displayInfo = actionLabels[action] || action || '';
            } else if (toolType === 'search_unlimited3d_knowledge_base' || 
                       (input.query && (input.top_k !== undefined || input.filters !== undefined))) {
              // Built-in KB or custom KB (detected by query + top_k/filters params)
              displayInfo = input.query ? input.query.substring(0, 40) + '...' : '';
            } else if (toolType === 'generate_image' || toolType === 'generate_image_pro') {
              // Show a preview of the prompt being used
              const prompt = input.prompt || '';
              displayInfo = prompt.length > 50 ? prompt.substring(0, 50) + '...' : prompt;
            }

            // Get or create tool group for this tool type
            const toolGroup = getOrCreateToolGroup(toolType);
            
            // Check if this is a custom KB tool (has query + top_k/filters params)
            const isCustomKbTool = input.query && (input.top_k !== undefined || input.filters !== undefined);
            
            const isHandoff = toolType.startsWith('handoff_to_');
            const isDelegation = toolType.startsWith('delegate_to_');
            toolGroup.icon = iconMap[toolType] || (isHandoff || isDelegation ? 'code' : isCustomKbTool ? 'search' : 'code');
            toolGroup.displayName = toolDisplayNames[toolType] || (isHandoff ? 'Handing Off' : isDelegation ? 'Delegating' : isCustomKbTool ? 'Knowledge Base' : toolType);
            
            // Store streaming content for live preview based on tool type.
            // Throttle regex extraction to every EXTRACT_THRESHOLD chars of growth.
            const EXTRACT_THRESHOLD2 = 1000;
            const rawInput = toolCall.rawInput || '';
            let streamingContent = '';
            let streamingLabel = toolType;

            const lastLen2 = toolGroup._lastExtractLen || 0;
            const needsExtract2 = !rawInput || (rawInput.length - lastLen2 >= EXTRACT_THRESHOLD2) || rawInput.length < EXTRACT_THRESHOLD2;
            
            if (toolType === 'sandboxed_file_write' || toolType === 'sandboxed_editor') {
              streamingLabel = input.path || input.file_path || toolGroup.streamingPath || 'Writing file...';
              if (!toolGroup._extractedPath && rawInput) {
                const head = rawInput.slice(0, 500);
                const pathMatch = head.match(/"path"\s*:\s*"([^"]+)"/);
                if (pathMatch) toolGroup._extractedPath = pathMatch[1];
              }
              if (toolGroup._extractedPath) streamingLabel = toolGroup._extractedPath;
            } else if (toolType === 'sandboxed_shell') {
              streamingContent = input.command || '';
              streamingLabel = 'Shell command';
              if (!streamingContent && rawInput && needsExtract2) {
                const cmdMatch = rawInput.match(/"command"\s*:\s*"([^"]+)"/);
                if (cmdMatch) streamingContent = cmdMatch[1];
                toolGroup._lastExtractLen = rawInput.length;
              }
            } else if (toolType === 'tasks') {
              const action = input.action || '';
              if (action === 'plan' || rawInput.includes('"plan"')) {
                streamingContent = input.description || '';
                streamingLabel = 'Planning tasks';
                if (!streamingContent && rawInput && needsExtract2) {
                  const descMatch = rawInput.match(/"description"\s*:\s*"((?:[^"\\]|\\.)*)(?:"|$)/);
                  if (descMatch) streamingContent = descMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"');
                  toolGroup._lastExtractLen = rawInput.length;
                }
              } else if (action === 'add') {
                streamingContent = input.description || '';
                streamingLabel = 'Adding task';
              } else if (action === 'done') {
                streamingContent = `Completing: ${input.task_id || ''}`;
                streamingLabel = 'Task progress';
              } else if (action === 'list') {
                streamingLabel = 'Checking tasks';
              }
            } else if (toolType === 'browser') {
              if (input.javascript) {
                streamingContent = input.javascript;
                streamingLabel = 'JavaScript execution';
              } else if (rawInput && needsExtract2) {
                const jsMatch = rawInput.match(/"javascript"\s*:\s*"((?:[^"\\]|\\.)*)(?:"|$)/);
                if (jsMatch) {
                  streamingContent = jsMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"');
                  streamingLabel = 'JavaScript execution';
                  toolGroup._lastExtractLen = rawInput.length;
                }
              }
            } else if (toolType === 'u3d_browser') {
              if (input.javascript) {
                streamingContent = input.javascript;
                streamingLabel = 'JavaScript execution';
              } else if (input.method) {
                streamingContent = `${input.method}(${JSON.stringify(input.params || {})})`;
                streamingLabel = 'Querying 3D scene';
              } else if (rawInput && needsExtract2) {
                const jsMatch = rawInput.match(/"javascript"\s*:\s*"((?:[^"\\]|\\.)*)(?:"|$)/);
                if (jsMatch) {
                  streamingContent = jsMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"');
                  streamingLabel = 'JavaScript execution';
                }
                toolGroup._lastExtractLen = rawInput.length;
              }
            } else if (toolType === 'search_unlimited3d_knowledge_base' || 
                       (input.query && (input.top_k !== undefined || input.filters !== undefined))) {
              streamingContent = input.query || '';
              streamingLabel = 'Knowledge Base Query';
              toolGroup.isKbTool = true;
            } else if (toolType === 'generate_image' || toolType === 'generate_image_pro') {
              streamingContent = input.prompt || '';
              streamingLabel = 'Image prompt';
            } else if (toolType.startsWith('handoff_to_')) {
              streamingContent = input.message || '';
              streamingLabel = `Handing off to ${input.agent_name || toolType.replace('handoff_to_', '')}`;
              if (!streamingContent && rawInput && needsExtract2) {
                const msgMatch = rawInput.match(/"message"\s*:\s*"((?:[^"\\]|\\.)*)(?:"|$)/);
                if (msgMatch) streamingContent = msgMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"');
                const agentMatch = rawInput.match(/"agent_name"\s*:\s*"([^"]+)"/);
                if (agentMatch) streamingLabel = `Handing off to ${agentMatch[1]}`;
                toolGroup._lastExtractLen = rawInput.length;
              }
            } else if (toolType.startsWith('delegate_to_')) {
              streamingContent = input.message || '';
              streamingLabel = `Delegating to ${toolType.replace('delegate_to_', '')}`;
              if (!streamingContent && rawInput && needsExtract2) {
                const msgMatch = rawInput.match(/"message"\s*:\s*"((?:[^"\\]|\\.)*)(?:"|$)/);
                if (msgMatch) streamingContent = msgMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"');
                toolGroup._lastExtractLen = rawInput.length;
              }
            }
            
            if (streamingContent) {
              toolGroup.streamingContent = streamingContent;
            }
            if (streamingLabel !== toolType || !toolGroup.streamingPath) {
              toolGroup.streamingPath = streamingLabel;
            }
            toolGroup.toolInput = input;

            const isFileOp2 = toolType === 'sandboxed_file_write' || toolType === 'sandboxed_editor';
            if (!isFileOp2) {
              toolGroup.rawInput = rawInput;
            }

            if (displayInfo && !toolGroup.items.includes(displayInfo)) {
              toolGroup.items.push(displayInfo);
            } else if (!displayInfo && toolGroup.items.length === 0) {
              toolGroup.items.push('');
            }

            if (isFileOp2) {
              if (toolGroup._pathRendered) return;
              if (toolGroup._extractedPath) toolGroup._pathRendered = true;
              scheduleFileRefresh();
            }

            scheduleContentUpdate();
          }
        }
      },

      onWorkflowComplete: () => {
        console.log('Workflow complete received');

        setActiveAgent(null);

        if (currentProject?.id && sessionIdRef.current) {
          loadTasks(currentProject.id, sessionIdRef.current);
        }

        if (!isGeneratingRef.current) {
          console.log('Agent already completed, skipping');
          return;
        }

        // Safety net: if message_created (assistant) didn't arrive yet (e.g.
        // lost event, AWS sync failure), build a message from streaming blocks.
        setMessages(prev => {
          const lastMsg = prev[prev.length - 1];
          const hasAssistant = lastMsg && lastMsg.type === 'assistant';
          if (hasAssistant) return prev; // already added by onMessageCreated

          console.log('[BuilderWorkspace] Fallback: building assistant message from streaming blocks');
          const finalBlocks = [...contentBlocksRef.current];
          finalBlocks.forEach(b => { b.completed = true; });

          let fullText = '';
          finalBlocks.forEach(b => {
            if (b.type === 'text') fullText += b.content;
          });

          return [...prev, {
            id: generateId(),
            type: 'assistant',
            content: fullText || 'Task completed.',
            contentBlocks: finalBlocks,
          }];
        });

        contentBlocksRef.current = [];
        toolInputAccRef.current = {};
        setContentBlocks([]);
        setIsGenerating(false);
        isGeneratingRef.current = false;
        setStreaming(false);

        flushFileRefresh();
        setPreviewKey(k => k + 1);

        // Cloud mode: force iframe src reload as HMR safety net
        if (process.env.REACT_APP_DEPLOYMENT_MODE === 'cloud' && previewIframeRef.current) {
          previewIframeRef.current.src = previewIframeRef.current.src;
        }

        if (wsRef.current) {
          wsRef.current.close();
          wsRef.current = null;
        }
      },

      onWorkflowError: (errorData) => {
        console.warn('Workflow error:', errorData);

        setActiveAgent(null);

        const errorType = errorData.error_type || 'unknown';
        const errorMessage = errorData.message || 'An error occurred during workflow execution';

        setMessages(prev => [...prev, {
          id: generateId(),
          type: 'warning',
          errorType: errorType,
          content: errorMessage,
        }]);

        contentBlocksRef.current = [];
        setContentBlocks([]);
        setIsGenerating(false);
        isGeneratingRef.current = false;
        setStreaming(false);

        flushFileRefresh();
        setPreviewKey(k => k + 1);

        if (wsRef.current) {
          wsRef.current.close();
          wsRef.current = null;
        }
      },

      onError: (err) => {
        console.error('Agent error:', err);
        
        // Clear active agent on error
        setActiveAgent(null);
        
        const errorMsg = typeof err === 'string' ? err : (err?.message || 'Agent execution failed');
        setError(errorMsg);
        setIsGenerating(false);
        isGeneratingRef.current = false;
        setStreaming(false);

        setMessages(prev => [...prev, {
          id: generateId(),
          type: 'error',
          content: errorMsg,
        }]);

        if (wsRef.current) {
          wsRef.current.close();
          wsRef.current = null;
        }
      },
      
      // ==========================================
      // Backend-managed message events
      // ==========================================

      // Backend created and persisted a message (user or assistant).
      // This is the PRIMARY way messages enter the frontend state.
      onMessageCreated: (message) => {
        console.log('[BuilderWorkspace] message_created:', message.role, message.id);
        const newMsg = {
          id: message.id,
          type: message.role === 'user' ? 'user' : 'assistant',
          content: message.content,
          attachments: message.attachments || [],
          contentBlocks: message.contentBlocks || [],
          commitShaBefore: message.commitShaBefore,
          commitShaAfter: message.commitShaAfter,
        };

        setMessages(prev => [...prev, newMsg]);

        // For assistant messages, clear streaming content blocks to prevent
        // dual-rendering — but preserve any pending interrupt blocks (e.g.
        // userQuestionnaire) that were pushed by onInterrupt. The interrupt
        // event and message_created can race, and clearing blindly would
        // destroy the questionnaire before it renders.
        if (message.role === 'assistant') {
          const interruptBlocks = contentBlocksRef.current.filter(
            b => b.type === 'userQuestionnaire' && !b.completed
          );
          contentBlocksRef.current = interruptBlocks;
          setContentBlocks([...interruptBlocks]);
        }
      },

      // Backend invalidated message cache — reload via local backend
      onCacheInvalidate: async (projectId, reason) => {
        console.log('[BuilderWorkspace] Cache invalidated:', projectId, reason);
        if (currentProject?.id) {
          const freshMessages = await loadMessages(currentProject.id);
          setMessages((freshMessages || []).map(m => ({
            id: m.id,
            type: m.role === 'user' ? 'user' : 'assistant',
            content: m.content,
            contentBlocks: m.contentBlocks || [],
            attachments: m.attachments || [],
            commitShaBefore: m.commitShaBefore,
            commitShaAfter: m.commitShaAfter,
          })));
        }
      },

      // Real-time task updates pushed by the backend (bypasses SDK streaming)
      onTaskEvent: (taskData) => {
        if (!taskData || !taskData._task_event) return;
        const evt = taskData._task_event;

        if (evt === 'plan' && Array.isArray(taskData.tasks)) {
          for (const t of taskData.tasks) {
            handleTaskEvent({
              event_type: 'task_added',
              data: { task: { id: t.id, content: t.description || t.content, status: t.status || 'pending', ...t } }
            });
          }
        } else if ((evt === 'add' || evt === 'update') && taskData.task) {
          const currentTasks = useTaskStore.getState().tasks;
          const exists = currentTasks.some(t => t.id === taskData.task.id);
          handleTaskEvent({
            event_type: exists ? 'task_updated' : 'task_added',
            data: { task: { id: taskData.task.id, content: taskData.task.description || taskData.task.content, status: taskData.task.status || 'pending', ...taskData.task } }
          });
        } else if (evt === 'done' && taskData.completed) {
          handleTaskEvent({
            event_type: 'task_updated',
            data: { task: { id: taskData.completed.id, content: taskData.completed.description || taskData.completed.content, status: 'completed', completed_by: taskData.completed.completed_by } }
          });
          if (taskData.next_task) {
            handleTaskEvent({
              event_type: 'task_updated',
              data: { task: { id: taskData.next_task.id, content: taskData.next_task.description || taskData.next_task.content, status: 'in_progress' } }
            });
          }
        }
      },

      onClose: () => {
        console.log('WebSocket closed, isGenerating:', isGeneratingRef.current);

        setActiveAgent(null);

        // If still generating when WS closes, the backend's disconnect cleanup
        // will persist the stopped assistant message to AWS. On next project open,
        // it will load via REST. For now, just clean up the UI.
        if (isGeneratingRef.current) {
          console.log('WebSocket closed while generating, cleaning up UI');

          // Preserve any accumulated streaming content as a stopped message
          const blocks = contentBlocksRef.current;
          if (blocks.length > 0) {
            const finalBlocks = [...blocks];
            finalBlocks.forEach(b => { b.completed = true; });
            let fullText = '';
            finalBlocks.forEach(b => { if (b.type === 'text') fullText += b.content; });
            setMessages(prev => {
              const lastMsg = prev[prev.length - 1];
              if (lastMsg && lastMsg.type === 'assistant') return prev;
              return [...prev, {
                id: generateId(),
                type: 'assistant',
                content: fullText || '(Connection lost)',
                contentBlocks: finalBlocks,
                stopped: true,
              }];
            });
          }

          contentBlocksRef.current = [];
          toolInputAccRef.current = {};
          setContentBlocks([]);
          setIsGenerating(false);
          isGeneratingRef.current = false;
          setStreaming(false);
          setIsReconnecting(false);
          setIsResuming(false);
          clearTasks();

          flushFileRefresh();
          setPreviewKey(k => k + 1);
        }

        wsRef.current = null;
      },

      onInterrupt: (data) => {
        console.log('[BuilderWorkspace] onInterrupt called:', JSON.stringify(data).slice(0, 500));
        const interruptId = data.interrupt_id;
        if (interruptId && handledInterruptIdsRef.current.has(interruptId)) {
          console.log('[BuilderWorkspace] Skipping already-handled interrupt:', interruptId);
          return;
        }
        if (data.interrupts?.length) {
          const interrupt = data.interrupts[0];
          console.log('[BuilderWorkspace] reason?.type:', interrupt.reason?.type);
          if (interrupt.reason?.type === 'user_questionnaire') {
            console.log('[BuilderWorkspace] Pushing userQuestionnaire block, questions:', interrupt.reason.questions?.length);
            if (interruptId) handledInterruptIdsRef.current.add(interruptId);
            const blocks = contentBlocksRef.current;
            blocks.push({
              type: 'userQuestionnaire',
              id: generateId(),
              interruptId,
              questions: interrupt.reason.questions,
              completed: false,
              answers: null,
            });
            scheduleContentUpdate();
          } else {
            console.warn('[BuilderWorkspace] Interrupt reason type mismatch:', interrupt.reason?.type);
          }
        } else {
          console.warn('[BuilderWorkspace] No interrupts in data or empty array');
        }
      },

      onReconnecting: (attempt, maxAttempts) => {
        console.log(`[BuilderWorkspace] Reconnecting... attempt ${attempt}/${maxAttempts}`);
        setIsReconnecting(true);
      },

      onReconnected: () => {
        console.log('[BuilderWorkspace] Reconnected successfully');
        setIsReconnecting(false);
        setIsResuming(false);
      },
    });

    wsRef.current = ws;

    try {
      if (resumeProjectId) {
        // Resume mode: reconnect to an active backend workflow after page refresh
        setIsResuming(true);
        console.log('[BuilderWorkspace] Resuming active workflow for project:', resumeProjectId);
        await ws.resume(resumeProjectId);
      } else {
        // Normal mode: start a new workflow
        await ws.connect();

        // Get the full workflow from the store (includes all agents and edges)
        const workflow = useAgentConfigStore.getState().getWorkflow();

        // Upload attachments via HTTP (avoids CloudFront WebSocket size limits)
        let preparedAttachments = [];
        if (attachments.length > 0) {
          const uploadResults = await Promise.all(
            attachments.map(a => projectApi.uploadAttachment(currentProject.id, a.file))
          );
          preparedAttachments = uploadResults.map(r => ({
            name: r.name,
            type: r.type,
            isImage: r.isImage,
            serverPath: r.path,
          }));
        }

        // WorkflowWebSocket expects: execute(workflow, task)
        // task is an object with project_id, task text, attachments, session_id, auth_token
        // NOTE: messages are NO LONGER sent - backend fetches from AWS directly (source of truth)
        const authToken = getAuthToken();
        await ws.execute(workflow, {
          project_id: currentProject.id,
          session_id: currentSessionId, // For task tracking
          task: task,
          // Backend now manages message history - fetches from AWS on workflow start
          // We only send attachments for the CURRENT message
          attachments: preparedAttachments,
          auth_token: authToken, // For thumbnail upload, message sync, and other AWS operations
        });
      }
    } catch (err) {
      console.error('WebSocket error:', err);
      setError(resumeProjectId
        ? 'Failed to resume workflow. The workflow may have completed.'
        : 'Failed to connect to agent. Is the backend running?');
      setIsGenerating(false);
      isGeneratingRef.current = false;
      setStreaming(false);
      setIsResuming(false);
    }
  }, [currentProject, flushFileRefresh, scheduleFileRefresh, isGenerating, setMessages, setStreaming]);

  // Check for active workflow on mount (resume after page refresh)
  useEffect(() => {
    if (!currentProject?.id || hasCheckedActiveWorkflowRef.current) return;
    if (isGeneratingRef.current || wsRef.current) return;

    hasCheckedActiveWorkflowRef.current = true;

    const checkActiveWorkflow = async () => {
      try {
        const result = await workflowApi.checkActive(currentProject.id);
        if (result.active) {
          console.log(
            `[BuilderWorkspace] Active workflow detected (type=${result.workflowType}, ` +
            `events=${result.eventCount}), resuming...`
          );
          executeAgent(null, [], { resumeProjectId: currentProject.id });
        }
      } catch (err) {
        console.debug('[BuilderWorkspace] Active workflow check failed:', err.message);
      }
    };

    const timer = setTimeout(checkActiveWorkflow, 500);
    return () => clearTimeout(timer);
  }, [currentProject?.id, executeAgent]);

  // Reset the active-workflow check flag when project changes
  useEffect(() => {
    hasCheckedActiveWorkflowRef.current = false;
  }, [currentProject?.id]);

  // Handle undo button click - show confirmation modal
  const handleUndoClick = useCallback((message) => {
    if (!message.commitShaBefore) {
      console.warn('No commit SHA to revert to for this message');
      setError('Cannot undo: No checkpoint was saved before this message. This can happen if the project wasn\'t synced to the cloud when the message was sent.');
      return;
    }
    setUndoTargetMessage(message);
    setUndoModalOpen(true);
  }, []);

  // Confirm and execute undo
  const handleUndoConfirm = useCallback(async () => {
    if (!undoTargetMessage) return;
    
    setUndoInProgress(true);
    try {
      const result = await undoToMessage(undoTargetMessage.id);
      if (result.success) {
        // Store the message content for autofill before removing
        const messageContent = undoTargetMessage.content;
        
        // Remove the clicked message and all messages after it from local state
        setMessages(prev => {
          const clickedIndex = prev.findIndex(m => m.id === undoTargetMessage.id);
          if (clickedIndex === -1) return prev;
          // Keep only messages before the clicked one
          return prev.slice(0, clickedIndex);
        });
        
        // Autofill the prompt with the undone message content
        setCurrentPrompt(messageContent);
        
        // Reload files and refresh preview
        await loadFiles();
        setPreviewKey(k => k + 1);
        setUndoModalOpen(false);
        setUndoTargetMessage(null);
      } else {
        setError(result.error || 'Undo failed');
      }
    } catch (err) {
      console.error('Undo error:', err);
      setError(err.message);
    } finally {
      setUndoInProgress(false);
    }
  }, [undoTargetMessage, undoToMessage, loadFiles, setMessages]);

  // Cancel undo
  const handleUndoCancel = useCallback(() => {
    setUndoModalOpen(false);
    setUndoTargetMessage(null);
  }, []);

  // Handle send message
  const handleSendMessage = useCallback(async (clearAttachmentsCallback) => {
    if ((!currentPrompt.trim() && currentAttachments.length === 0) || isGenerating) return;

    // Reset scroll-up flag - user wants to see new content
    userScrolledUpRef.current = false;

    // Store attachments for the message before clearing
    const attachmentsForMessage = [...currentAttachments];

    // User message will appear via message_created event from backend
    // (backend syncs to AWS immediately, then sends event with AWS UUID)
    executeAgent(currentPrompt, attachmentsForMessage);
    setCurrentPrompt('');

    // Clear attachments in PromptInputBar
    if (clearAttachmentsCallback) {
      clearAttachmentsCallback();
    }
    setCurrentAttachments([]);
  }, [currentPrompt, currentAttachments, isGenerating, executeAgent]);

  // Handle start Vite
  const handleStartVite = useCallback(async () => {
    try {
      await startVite();
    } catch (err) {
      setError('Failed to start Vite: ' + err.message);
    }
  }, [startVite]);

  // Handle stop Vite
  const handleStopVite = useCallback(async () => {
    try {
      await stopVite();
    } catch (err) {
      console.error('Failed to stop Vite:', err);
    }
  }, [stopVite]);

  // Handle stop agent execution
  const handleStopExecution = useCallback(async () => {
    isGeneratingRef.current = false;
    setIsReconnecting(false);
    setIsResuming(false);

    // Tell the backend to cancel the running executor task.
    // This works for both multi-agent (background task) and will be a no-op
    // if there's no active session (e.g. single-agent already finished).
    if (currentProject?.id) {
      try {
        await workflowApi.stop(currentProject.id);
      } catch (err) {
        console.warn('[BuilderWorkspace] Stop request failed (may already be finished):', err);
      }
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setActiveAgent(null);

    if (currentProject?.id && sessionIdRef.current) {
      loadTasks(currentProject.id, sessionIdRef.current);
    }

    toolInputAccRef.current = {};

    // Preserve accumulated streaming content as a stopped assistant message
    // so the user can see what the agent produced before being stopped.
    const stoppedBlocks = contentBlocksRef.current;
    if (stoppedBlocks.length > 0) {
      const finalBlocks = [...stoppedBlocks];
      finalBlocks.forEach(b => { b.completed = true; });
      let fullText = '';
      finalBlocks.forEach(b => { if (b.type === 'text') fullText += b.content; });

      setMessages(prev => {
        const lastMsg = prev[prev.length - 1];
        if (lastMsg && lastMsg.type === 'assistant') return prev;
        return [...prev, {
          id: generateId(),
          type: 'assistant',
          content: fullText || '(Stopped by user)',
          contentBlocks: finalBlocks,
          stopped: true,
        }];
      });
    }

    contentBlocksRef.current = [];
    setContentBlocks([]);
    setIsGenerating(false);
    setStreaming(false);

    flushFileRefresh();
    setPreviewKey(k => k + 1);
  }, [currentProject?.id, loadTasks, flushFileRefresh, setMessages, setStreaming]);

  return (
    <div className={`builder-workspace ${!chatSidebarOpen ? 'chat-collapsed' : ''}`}>
      {/* Left Section - Chat/Prompt Area */}
      <div className={`builder-chat-panel ${!chatSidebarOpen ? 'collapsed' : ''}`}>
        {/* Error Banner */}
        {error && (
          <div className="builder-error-banner" onClick={() => setError(null)}>
            <span>⚠️ {formatErrorMessage(error)}</span>
            <button>✕</button>
          </div>
        )}

        {/* Task Panel - shows agent objectives/todos (hidden until first task exists) */}
        {currentProject?.id && sessionId && tasks.length > 0 && (
          <TaskPanel
            projectId={currentProject.id}
            sessionId={sessionId}
          />
        )}

        {/* Messages - scrollable */}
        <div 
          className="builder-chat-messages"
          ref={messagesContainerRef}
          onScroll={handleScroll}
        >
          {messages.map((message) => (
            <React.Fragment key={message.id}>
              {message.type === 'user' ? (
                <div className="builder-message-user-wrapper">
                  <div className="builder-message-user">
                    {/* Show attachments if any */}
                    {message.attachments && message.attachments.length > 0 && (
                      <div className="message-attachments">
                        {message.attachments.map((att, idx) => {
                          const imgSrc = att.isImage && (
                            att.preview
                              ? (att.preview.startsWith('data:') ? att.preview : `data:${att.type || 'image/png'};base64,${att.preview}`)
                              : att.serverPath && currentProject
                                ? projectApi.getRawFileUrl(currentProject.id, att.serverPath)
                                : null
                          );
                          return (
                          <div key={idx} className="message-attachment-thumb">
                            {imgSrc ? (
                              <img
                                src={imgSrc}
                                alt={att.name}
                              />
                            ) : (
                              <div className="message-attachment-file">
                                <span className="file-icon">📎</span>
                                <span className="file-name">{att.name}</span>
                              </div>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    )}
                    <p>{normalizeContent(message.content)}</p>
                    {/* Undo button - appears on hover if message has commit info */}
                    {message.commitShaBefore && !isGenerating && (
                      <button 
                        className="message-undo-button"
                        onClick={() => handleUndoClick(message)}
                        title="Undo to this point"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 7v6h6"/>
                          <path d="M21 17a9 9 0 0 0-15-6.7L3 13"/>
                        </svg>
                        <span>Undo</span>
                      </button>
                    )}
                  </div>
                </div>
              ) : message.type === 'error' ? (
                <div className="builder-message-error">
                  <p>❌ {message.content}</p>
                </div>
              ) : message.type === 'warning' ? (
                <div className="builder-message-warning">
                  <div className="warning-icon">⚠️</div>
                  <div className="warning-content">
                    <p className="warning-title">
                      {message.errorType === 'max_tokens' ? 'Output Limit Reached' :
                       message.errorType === 'context_overflow' ? 'Context Too Long' :
                       message.errorType === 'throttled' ? 'Rate Limited' :
                       message.errorType === 'timeout' ? 'Request Timed Out' :
                       message.errorType === 'node_timeout' ? 'Agent Timed Out' :
                       message.errorType === 'stream_stall' ? 'Response Interrupted' :
                       message.errorType === 'connection' ? 'Connection Error' :
                       message.errorType === 'tool_error' ? 'Tool Execution Failed' :
                       message.errorType === 'auth' ? 'Authentication Error' :
                       message.errorType === 'event_loop' ? 'Agent Error' :
                       'Workflow Stopped'}
                    </p>
                    <p className="warning-message">{message.content}</p>
                    <p className="warning-hint">You can continue the conversation with a new message.</p>
                  </div>
                </div>
              ) : (
                <div className="builder-message-assistant">
                  {/* Use contentBlocks if available for proper hierarchy, otherwise fallback */}
                  {message.contentBlocks && message.contentBlocks.length > 0 ? (
                    // Render blocks in order - preserves text/tool interleaving
                    message.contentBlocks.map((block) => (
                      <React.Fragment key={block.id}>
                        {block.type === 'text' && block.content && (
                          <div className="markdown-content">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{stripGeminiCtrl(block.content)}</ReactMarkdown>
                          </div>
                        )}
                        {block.type === 'agentMarker' && (
                          <div className="agent-transition-marker">
                            <div className="agent-marker-line" />
                            <div className="agent-marker-badge">
                              <span className="agent-marker-name">{block.agentName}</span>
                            </div>
                            <div className="agent-marker-line" />
                          </div>
                        )}
                        {block.type === 'toolGroup' && (
                          <div className={`builder-generation-steps collapsed ${expandedTools[block.id] ? 'expanded' : ''}`}>
                            <div 
                              className={`builder-step completed ${block.streamingContent ? 'clickable' : ''}`}
                              onClick={() => {
                                if (block.streamingContent) {
                                  setExpandedTools(prev => ({
                                    ...prev,
                                    [block.id]: !prev[block.id]
                                  }));
                                }
                              }}
                            >
                              <img
                                src={`/assets/builder/${block.icon === 'terminal' ? 'code-terminal' : 'code'}.svg`}
                                alt=""
                                className="builder-step-icon"
                              />
                              <span className="step-text">
                                {(() => {
                                  const displayFiles = (block.items || []).filter(f => f).slice(0, 3);
                                  const moreCount = (block.items || []).length - 3;
                                  const filesText = displayFiles.map(f => `\`${f}\``).join(', ');
                                  const suffix = moreCount > 0 ? ` +${moreCount} more` : '';
                                  return filesText
                                    ? `${block.displayName || block.toolType}: ${filesText}${suffix}`
                                    : block.displayName || block.toolType;
                                })()}
                              </span>
                              <span className="step-check">✓</span>
                              {block.streamingContent && (
                                <span className="step-expand-icon">{expandedTools[block.id] ? '▼' : '▶'}</span>
                              )}
                            </div>
                            {expandedTools[block.id] && block.streamingContent && (
                              <div className="tool-streaming-preview">
                                {block.streamingPath && (
                                  <div className="streaming-path">{block.streamingPath}</div>
                                )}
                                <pre className="streaming-code">
                                  <code>
                                    {(() => {
                                      const content = block.streamingContent || '';
                                      const lines = content.split('\n');
                                      const maxLines = 12;
                                      if (lines.length > maxLines) {
                                        return `...\n${lines.slice(-maxLines).join('\n')}`;
                                      }
                                      return content;
                                    })()}
                                  </code>
                                </pre>
                              </div>
                            )}
                          </div>
                        )}
                        {block.type === 'image' && (
                          <div className="builder-image-result">
                            <div className="image-thumbnail" onClick={() => setSelectedImage(block.src)}>
                              <img src={block.src} alt={block.alt} />
                              <div className="image-overlay">
                                <span>🔍 View Design</span>
                              </div>
                            </div>
                          </div>
                        )}
                        {block.type === 'userQuestionnaire' && !block.completed && (
                          <DesignQuestionnaire
                            questions={block.questions}
                            onSubmit={(answers) => {
                              if (wsRef.current) {
                                wsRef.current.sendInterruptResponse(
                                  block.interruptId,
                                  answers
                                );
                              }
                              block.completed = true;
                              block.answers = answers;
                              scheduleContentUpdate();
                            }}
                            onSkip={() => {
                              if (wsRef.current) {
                                wsRef.current.sendInterruptResponse(
                                  block.interruptId,
                                  { skipped: true }
                                );
                              }
                              block.completed = true;
                              block.answers = { skipped: true };
                              scheduleContentUpdate();
                            }}
                          />
                        )}
                        {block.type === 'userQuestionnaire' && block.completed && (
                          <DesignQuestionnaireSummary answers={block.answers} />
                        )}
                      </React.Fragment>
                    ))
                  ) : (
                    // Fallback: render combined content + steps (old format)
                    <>
                      <div className="markdown-content">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{normalizeContent(message.content)}</ReactMarkdown>
                      </div>
                      {message.steps && message.steps.length > 0 && (
                        <div className="builder-generation-steps collapsed">
                          {message.steps.map((step) => (
                            <div key={step.id} className="builder-step completed">
                              <img
                                src={`/assets/builder/${step.icon === 'terminal' ? 'code-terminal' : 'code'}.svg`}
                                alt=""
                                className="builder-step-icon"
                              />
                              <span className="step-text">
                                {step.text}
                              </span>
                              <span className="step-check">✓</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </React.Fragment>
          ))}

          {/* Resuming / Reconnecting banners */}
          {isResuming && !isReconnecting && (
            <div className="builder-reconnecting-banner builder-resuming-banner">
              <span className="reconnecting-spinner" />
              <span>Resuming workflow...</span>
            </div>
          )}
          {isReconnecting && (
            <div className="builder-reconnecting-banner">
              <span className="reconnecting-spinner" />
              <span>Connection interrupted — reconnecting...</span>
            </div>
          )}

          {/* Streaming content blocks - renders text and tools in order */}
          {(isGenerating || isReconnecting || isResuming) && contentBlocks.length > 0 && (
            <div className="builder-message-assistant streaming">
              {/* Active agent badge removed - task UI already shows current agent */}
              {contentBlocks.map((block, index) => {
                const isLast = index === contentBlocks.length - 1;
                const isActive = !block.completed && isLast;

                return (
                  <React.Fragment key={block.id}>
                    {block.type === 'text' && block.content && (
                      <div className={`markdown-content ${block.completed ? 'completed' : ''}`}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{stripGeminiCtrl(block.content)}</ReactMarkdown>
                      </div>
                    )}
                    {block.type === 'toolGroup' && (
                      <div className={`builder-generation-steps ${block.completed ? 'collapsed' : 'active'} ${expandedTools[block.id] ? 'expanded' : ''}`}>
                        <div 
                          className={`builder-step ${block.completed ? 'completed' : 'in-progress'} ${block.completed && (block.streamingContent || block.kbResult) ? 'clickable' : ''}`}
                          onClick={() => {
                            if (block.completed && (block.streamingContent || block.kbResult)) {
                              setExpandedTools(prev => ({
                                ...prev,
                                [block.id]: !prev[block.id]
                              }));
                            }
                          }}
                        >
                          <img
                            src={`/assets/builder/${block.icon === 'terminal' ? 'code-terminal' : block.kbResult ? 'search' : 'code'}.svg`}
                            alt=""
                            className="builder-step-icon"
                          />
                          <span className="step-text">
                            {(() => {
                              const displayFiles = block.items.filter(f => f).slice(0, 3);
                              const moreCount = block.items.length - 3;
                              const filesText = displayFiles.map(f => `\`${f}\``).join(', ');
                              const suffix = moreCount > 0 ? ` +${moreCount} more` : '';
                              return filesText
                                ? `${block.displayName}: ${filesText}${suffix}`
                                : block.displayName;
                            })()}
                          </span>
                          {block.completed ? (
                            <>
                              <span className="step-check">✓</span>
                              {(block.streamingContent || block.kbResult) && (
                                <span className="step-expand-icon">{expandedTools[block.id] ? '▼' : '▶'}</span>
                              )}
                            </>
                          ) : (
                            <span className="step-spinner"></span>
                          )}
                        </div>
                        {/* KB Result - show as formatted markdown when expanded */}
                        {expandedTools[block.id] && block.kbResult && (
                          <div className="tool-kb-result">
                            <div className="kb-result-header">
                              <img src="/assets/builder/search.svg" alt="" className="kb-header-icon" />
                              <span>Knowledge Base Results</span>
                            </div>
                            <div className="kb-result-content markdown-content">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>{block.kbResult}</ReactMarkdown>
                            </div>
                          </div>
                        )}
                        {/* Streaming content preview - always mounted to avoid DOM flicker, hidden via display */}
                        {block.streamingContent && (
                          <div
                            className="tool-streaming-preview"
                            style={{
                              display: (!block.completed || (expandedTools[block.id] && !block.kbResult)) ? 'flex' : 'none'
                            }}
                          >
                            {block.streamingPath && (
                              <div className="streaming-path">{block.streamingPath}</div>
                            )}
                            <pre className="streaming-code">
                              <code>
                                {(() => {
                                  const content = block.streamingContent || '';
                                  const lines = content.split('\n');
                                  const maxLines = 12;
                                  if (lines.length > maxLines) {
                                    return `...\n${lines.slice(-maxLines).join('\n')}`;
                                  }
                                  return content;
                                })()}
                              </code>
                            </pre>
                          </div>
                        )}
                      </div>
                    )}
                    {block.type === 'image' && (
                      <div className="builder-image-result">
                        <div className="image-thumbnail" onClick={() => setSelectedImage(block.src)}>
                          <img src={block.src} alt={block.alt} />
                          <div className="image-overlay">
                            <span>🔍 View Design</span>
                          </div>
                        </div>
                      </div>
                    )}
                    {block.type === 'userQuestionnaire' && !block.completed && (
                      <DesignQuestionnaire
                        questions={block.questions}
                        onSubmit={(answers) => {
                          if (wsRef.current) {
                            wsRef.current.sendInterruptResponse(
                              block.interruptId,
                              answers
                            );
                          }
                          block.completed = true;
                          block.answers = answers;
                          scheduleContentUpdate();
                        }}
                        onSkip={() => {
                          if (wsRef.current) {
                            wsRef.current.sendInterruptResponse(
                              block.interruptId,
                              { skipped: true }
                            );
                          }
                          block.completed = true;
                          block.answers = { skipped: true };
                          scheduleContentUpdate();
                        }}
                      />
                    )}
                    {block.type === 'userQuestionnaire' && block.completed && (
                      <DesignQuestionnaireSummary answers={block.answers} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Spacer */}
        <div className="builder-chat-spacer" />

        {/* Prompt Input Bar */}
        <PromptInputBar
          value={currentPrompt}
          onChange={setCurrentPrompt}
          onSubmit={handleSendMessage}
          onStop={handleStopExecution}
          onAttachmentsChange={setCurrentAttachments}
          isExecuting={isGenerating}
          experienceId={currentProject?.id || experience?.id || 'new'}
          placeholder="Describe what you want to build..."
          disabled={!currentProject}
          projectTemplate={currentProject?.template}
        />
      </div>

      {/* Right Section - Preview/Code Area */}
      <div className="builder-preview-panel">
        {/* Preview toolbar - hidden for cleaner UI, preview auto-handles itself */}
        {false && (
        <div className="builder-preview-header">
          <div className="preview-status">
            {viewMode === 'preview' ? (
              <>
                <span className={`status-dot ${viteLoading ? 'loading' : viteStatus?.running ? 'running' : 'stopped'}`} />
                <span>
                  {viteLoading
                    ? viteLoadingMessage || 'Starting...'
                    : viteStatus?.running
                      ? `Running on port ${viteStatus.port}`
                      : 'Preview stopped'}
                </span>
                {/* Unlimited3D indicator - browser tool is always available */}
                {currentProject?.template === 'unlimited3d' && viteStatus?.running && (
                  <span
                    className="u3d-browser-status"
                    title="AI can control headless browser to view and interact with 3D scene"
                  >
                    🎮 3D
                  </span>
                )}
              </>
            ) : (
              <span>Code Editor</span>
            )}
          </div>
          <div className="preview-actions">
            {viewMode === 'preview' && (
              <>
                {viteLoading ? (
                  <button className="preview-action-btn" disabled>
                    <span className="btn-spinner"></span>
                    Starting...
                  </button>
                ) : !viteStatus?.running ? (
                  <button className="preview-action-btn" onClick={handleStartVite}>
                    Start Preview
                  </button>
                ) : (
                  <>
                    <button className="preview-action-btn" onClick={() => setPreviewKey(k => k + 1)}>
                      Refresh
                    </button>
                    <button className="preview-action-btn stop" onClick={handleStopVite}>
                      Stop
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
        )}
        <div className="builder-preview-container">
          {/* Code Editor - always mounted to preserve state */}
          <div className={`code-editor-wrapper ${viewMode === 'code' ? 'visible' : 'hidden'}`}>
            <CodeEditorPanel />
          </div>

          {/* Preview - always mounted to preserve iframe state */}
          <div className={`preview-wrapper ${viewMode === 'preview' ? 'visible' : 'hidden'} device-${deviceMode}`}>
            {viteLoading ? (
              <div className="builder-preview-loading">
                <div className="preview-loader">
                  <div className="loader-spinner"></div>
                  <h3>{viteLoadingMessage || 'Starting preview server...'}</h3>
                  <p>This may take a moment if dependencies need to be installed</p>
                </div>
              </div>
            ) : viteUrl ? (
              <div className="device-frame-container">
                {deviceMode !== 'desktop' && (
                  <div className="device-label">{DEVICE_SIZES[deviceMode]?.label} — {DEVICE_SIZES[deviceMode]?.width}×{DEVICE_SIZES[deviceMode]?.height}</div>
                )}
                <div 
                  className={`device-frame device-${deviceMode}`}
                  style={deviceMode !== 'desktop' ? {
                    width: DEVICE_SIZES[deviceMode]?.width,
                    height: DEVICE_SIZES[deviceMode]?.height,
                  } : {}}
                >
                  <iframe
                    ref={previewIframeRef}
                    key={previewKey}
                    src={viteUrl}
                    className="builder-preview-iframe"
                    title="Project Preview"
                    sandbox="allow-scripts allow-same-origin allow-forms"
                  />
                </div>
              </div>
            ) : (
              <div className="builder-preview-placeholder">
                <div className="placeholder-content">
                  <img src="/assets/builder/code.svg" alt="" className="placeholder-icon" />
                  <h3>
                    {viteLoading ? 'Starting Preview...' :
                      viteStatus?.error ? 'Preview Error' :
                        'Starting preview...'}
                  </h3>
                  <p>
                    {viteLoading ? viteLoadingMessage :
                      (viteStatus?.error ? formatErrorMessage(viteStatus.error) : null) || 'The preview server will start automatically'}
                  </p>
                  {viteStatus?.error?.includes('npm not found') && (
                    <div className="npm-install-help">
                      <p>Node.js and npm are required for the live preview.</p>
                      <a
                        href="https://nodejs.org/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="preview-action-btn"
                      >
                        Download Node.js
                      </a>
                      <p className="placeholder-hint">After installing, restart the backend server.</p>
                    </div>
                  )}
                  {viteStatus?.error && !viteStatus.error.includes('npm not found') && (
                    <button className="preview-action-btn retry" onClick={handleStartVite}>
                      Retry
                    </button>
                  )}
                  {!currentProject && (
                    <p className="placeholder-hint">Create a project first to begin building</p>
                  )}
                  {viteLoading && (
                    <div className="vite-loading-spinner">
                      <div className="spinner"></div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Overlay actions — visible on hover */}
            {viteUrl && !viteLoading && (
              <div className="preview-overlay-actions">
                <button
                  className="preview-overlay-btn"
                  onClick={() => setPreviewKey(k => k + 1)}
                  title="Reload preview"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10" />
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                  </svg>
                </button>
                <button
                  className="preview-overlay-btn"
                  onClick={() => window.open(viteUrl, '_blank')}
                  title="Open in new tab"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Undo Confirmation Modal */}
      {undoModalOpen && (
        <div className="undo-modal-overlay" onClick={handleUndoCancel}>
          <div className="undo-modal" onClick={e => e.stopPropagation()}>
            <div className="undo-modal-header">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 7v6h6"/>
                <path d="M21 17a9 9 0 0 0-15-6.7L3 13"/>
              </svg>
              <h3>Undo Changes?</h3>
            </div>
            <div className="undo-modal-body">
              <p>
                This will revert all project files to the state <strong>before</strong> this message was sent.
              </p>
              <p className="undo-warning">
                ⚠️ All changes made after this message will be lost. This action cannot be undone.
              </p>
              {undoTargetMessage && (
                <div className="undo-message-preview">
                  <span className="undo-preview-label">Reverting to before:</span>
                  <span className="undo-preview-content">
                    "{undoTargetMessage.content.slice(0, 100)}{undoTargetMessage.content.length > 100 ? '...' : ''}"
                  </span>
                </div>
              )}
            </div>
            <div className="undo-modal-actions">
              <button 
                className="undo-cancel-btn"
                onClick={handleUndoCancel}
                disabled={undoInProgress}
              >
                Cancel
              </button>
              <button 
                className="undo-confirm-btn"
                onClick={handleUndoConfirm}
                disabled={undoInProgress}
              >
                {undoInProgress ? (
                  <>
                    <span className="btn-spinner"></span>
                    Reverting...
                  </>
                ) : (
                  'Undo Changes'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BuilderWorkspace;

